import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../../../config.json";
import {
  MAX_PRODUCT_BADGE_COUNT,
  PRODUCT_BADGE_OPTIONS,
  getProductBadgeOption,
  normalizeProductBadgesForSave,
} from "../../../constants/productBadgeOptions";
import {
  ZONE_COUNTRY_OPTIONS,
  getZoneCountryFlagUrl,
  getZoneCountryLabel,
  isValidZoneCountryCode,
} from "../../../constants/zoneCountryOptions";
import "./ProductBadgesEditor.css";
import "./ProductExtras.css";

const COUNTRY_BADGE_TYPE = "zone";

function buildDraftFromValues(values) {
  const map = new Map();
  normalizeProductBadgesForSave(values).forEach((item) => {
    map.set(item.type, item.value || "");
  });
  return map;
}

function getBadgeDisplayLabel(type, value) {
  const option = getProductBadgeOption(type);
  if (type === "customized" && value) return value;
  if (type === COUNTRY_BADGE_TYPE && value) return getZoneCountryLabel(value, "en");
  return option.label;
}

function renderBadgeSummaryIcon(type, value) {
  if (type === COUNTRY_BADGE_TYPE && value) {
    return (
      <img
        src={getZoneCountryFlagUrl(value)}
        alt=""
        className="product-badges-summary-chip__flag"
      />
    );
  }
  const option = getProductBadgeOption(type);
  return (
    <span className="product-badges-summary-chip__icon" aria-hidden="true">
      {option.icon}
    </span>
  );
}

export default function ProductBadgesEditor({
  values = [],
  onChange,
  productId = null,
  persistOnSave = false,
}) {
  const [showModal, setShowModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [draftMap, setDraftMap] = useState(() => buildDraftFromValues(values));
  const [saving, setSaving] = useState(false);

  const savedValues = normalizeProductBadgesForSave(values);

  useEffect(() => {
    if (!showModal && !showCountryModal) {
      setDraftMap(buildDraftFromValues(values));
    }
  }, [values, showModal, showCountryModal]);

  const openModal = () => {
    setDraftMap(buildDraftFromValues(values));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setShowCountryModal(false);
    setDraftMap(buildDraftFromValues(values));
  };

  const selectDraftType = (type) => {
    setDraftMap((prev) => {
      const next = new Map(prev);
      if (next.has(type)) {
        next.delete(type);
        return next;
      }
      if (next.size >= MAX_PRODUCT_BADGE_COUNT) {
        toast.warning(`You can select up to ${MAX_PRODUCT_BADGE_COUNT} badges.`);
        return prev;
      }
      next.set(type, "");
      return next;
    });
  };

  const handleBadgeCardClick = (option) => {
    if (option.value === COUNTRY_BADGE_TYPE) {
      if (!draftMap.has(COUNTRY_BADGE_TYPE) && draftMap.size >= MAX_PRODUCT_BADGE_COUNT) {
        toast.warning(`You can select up to ${MAX_PRODUCT_BADGE_COUNT} badges.`);
        return;
      }
      if (!draftMap.has(COUNTRY_BADGE_TYPE)) {
        setDraftMap((prev) => {
          const next = new Map(prev);
          next.set(COUNTRY_BADGE_TYPE, "");
          return next;
        });
      }
      setShowCountryModal(true);
      return;
    }
    selectDraftType(option.value);
  };

  const removeDraftType = (type) => {
    setDraftMap((prev) => {
      const next = new Map(prev);
      next.delete(type);
      return next;
    });
    if (type === COUNTRY_BADGE_TYPE) {
      setShowCountryModal(false);
    }
  };

  const updateDraftValue = (type, nextValue) => {
    setDraftMap((prev) => {
      const next = new Map(prev);
      if (!next.has(type)) return next;
      next.set(type, nextValue);
      return next;
    });
  };

  const persistValues = async (nextValues) => {
    if (!persistOnSave || !productId) return true;

    const token = localStorage.getItem("tokenLogin");
    await axios.put(
      `${config.dev}/v2/products/edit/${productId}`,
      { productBadges: nextValues, productBadge: "" },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return true;
  };

  const removeSavedItem = async (type) => {
    const nextValues = savedValues.filter((item) => item.type !== type);
    onChange(nextValues);

    if (persistOnSave && productId) {
      try {
        await persistValues(nextValues);
        toast.success("Product badge removed.");
      } catch (error) {
        console.error("Failed to remove product badge:", error);
        toast.error(
          error.response?.data?.message || "Failed to update product badges."
        );
      }
    }
  };

  const handleSave = async () => {
    const nextValues = normalizeProductBadgesForSave(
      Array.from(draftMap.entries()).map(([type, value]) => ({ type, value }))
    );

    if (
      draftMap.has("customized") &&
      !String(draftMap.get("customized") || "").trim()
    ) {
      toast.error("Please enter a custom badge label.");
      return;
    }

    if (
      draftMap.has(COUNTRY_BADGE_TYPE) &&
      !isValidZoneCountryCode(draftMap.get(COUNTRY_BADGE_TYPE))
    ) {
      toast.error("Please choose a country flag for the Country badge.");
      setShowCountryModal(true);
      return;
    }

    onChange(nextValues);

    if (persistOnSave && productId) {
      setSaving(true);
      try {
        await persistValues(nextValues);
        toast.success("Product badges saved. They are now visible to clients.");
      } catch (error) {
        console.error("Failed to save product badges:", error);
        toast.error(
          error.response?.data?.message || "Failed to save product badges."
        );
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    setShowCountryModal(false);
    setShowModal(false);
  };

  const handleCountrySelect = (countryCode) => {
    updateDraftValue(COUNTRY_BADGE_TYPE, countryCode);
    setShowCountryModal(false);
  };

  const draftCustomizedSelected = draftMap.has("customized");
  const draftCountrySelected = draftMap.has(COUNTRY_BADGE_TYPE);
  const draftCountryCode = draftMap.get(COUNTRY_BADGE_TYPE) || "";
  const draftCount = draftMap.size;
  const atMaxSelection = draftCount >= MAX_PRODUCT_BADGE_COUNT;

  return (
    <div className="prod-extra-editor product-badges-editor">
      <div className="prod-extra-card">
      <button type="button" className="prod-extra-trigger" onClick={openModal}>
        <span className="prod-extra-trigger__icon" aria-hidden>
          <i className="fas fa-award" />
        </span>
        <span className="prod-extra-trigger__copy">
          <span className="prod-extra-trigger__title">Badges</span>
          <span className="prod-extra-trigger__hint">
            Shown on product cards · max {MAX_PRODUCT_BADGE_COUNT}
          </span>
        </span>
        {savedValues.length > 0 && (
          <span className="prod-extra-trigger__count">{savedValues.length}</span>
        )}
        <i className="fas fa-chevron-right prod-extra-trigger__chevron" aria-hidden />
      </button>

      {savedValues.length > 0 && (
        <div className="product-badges-summary prod-extra-card__chips">
          {savedValues.map((item) => {
            const option = getProductBadgeOption(item.type);
            return (
              <span key={item.type} className="product-badges-summary-chip">
                {renderBadgeSummaryIcon(item.type, item.value)}
                <span className="product-badges-summary-chip__text">
                  {getBadgeDisplayLabel(item.type, item.value)}
                </span>
                <button
                  type="button"
                  className="product-badges-summary-chip__remove"
                  onClick={() => removeSavedItem(item.type)}
                  aria-label={`Remove ${option.label}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      </div>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        className="prod-extra-modal product-badges-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Product badges</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="product-badges-modal__intro">
            Choose up to {MAX_PRODUCT_BADGE_COUNT}. Country opens a flag picker.
          </p>
          <p className="product-badges-modal__counter">
            {draftCount} / {MAX_PRODUCT_BADGE_COUNT} selected
          </p>

          <div className="product-badges-picker-grid">
            {PRODUCT_BADGE_OPTIONS.map((option) => {
              const isCountry = option.value === COUNTRY_BADGE_TYPE;
              const isSelected = draftMap.has(option.value);
              const isDisabled = !isSelected && atMaxSelection && !isCountry;
              const countryFlagUrl =
                isCountry && draftCountryCode
                  ? getZoneCountryFlagUrl(draftCountryCode)
                  : "";
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`product-badges-picker-card${isSelected ? " is-selected" : ""}${isDisabled ? " is-disabled" : ""}`}
                  onClick={() => handleBadgeCardClick(option)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                >
                  {countryFlagUrl ? (
                    <img
                      src={countryFlagUrl}
                      alt=""
                      className="product-badges-picker-card__flag"
                    />
                  ) : (
                    <span className="product-badges-picker-card__icon" aria-hidden="true">
                      {option.icon}
                    </span>
                  )}
                  <span className="product-badges-picker-card__label">
                    {option.label}
                  </span>
                  <span className="product-badges-picker-card__description">
                    {isCountry && draftCountryCode
                      ? getZoneCountryLabel(draftCountryCode, "en")
                      : option.description}
                  </span>
                </button>
              );
            })}
          </div>

          {draftCustomizedSelected && (
            <div className="product-badges-custom-list">
              <h6 className="product-badges-custom-list__title">Custom badge label</h6>
              <div className="product-badges-custom-row">
                <div className="product-badges-custom-row__label">
                  <span aria-hidden="true">🏷️</span>
                  <span>Customized</span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter custom badge label"
                  value={draftMap.get("customized") || ""}
                  onChange={(e) => updateDraftValue("customized", e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeDraftType("customized")}
                  aria-label="Remove customized badge"
                >
                  <i className="fa fa-trash" />
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCountryModal}
        onHide={() => setShowCountryModal(false)}
        centered
        className="prod-extra-modal product-badges-modal product-badges-country-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose country flag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="product-badges-modal__intro">
            The flag will appear on the product card.
          </p>
          <div className="product-badges-zone-grid">
            {ZONE_COUNTRY_OPTIONS.map((country) => {
              const isSelected = draftCountryCode === country.code;
              return (
                <button
                  key={country.code}
                  type="button"
                  className={`product-badges-zone-card${isSelected ? " is-selected" : ""}`}
                  onClick={() => handleCountrySelect(country.code)}
                  aria-pressed={isSelected}
                >
                  <img
                    src={getZoneCountryFlagUrl(country.code)}
                    alt=""
                    className="product-badges-zone-card__flag"
                  />
                  <span className="product-badges-zone-card__label">
                    {country.name.en}
                  </span>
                </button>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {draftCountrySelected ? (
            <Button
              variant="outline-danger"
              className="me-auto"
              onClick={() => removeDraftType(COUNTRY_BADGE_TYPE)}
            >
              Remove Country badge
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => setShowCountryModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
